import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Star, LogOut, MessageSquare, TrendingUp, IndianRupee, BadgeCheck, 
  Check, X, Send, Bell, CheckCircle, XCircle, CheckCheck, Clock, 
  AlertCircle, Package, Wallet, Gift, Share2, Users, Trophy, Copy, 
  Camera, Film, Music, Mic, PenTool, Sparkles, Plus, Edit2, Loader2, 
  Globe, MapPin, Instagram, Youtube, Facebook, Crown, Building2,
  Smartphone, Calendar, FileText, CreditCard, Image as ImageIcon,
  Clapperboard, Gamepad2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import BottomNav from "@/components/BottomNav";
import ChatPanel from "@/components/chat/ChatPanel";
import ShareProfileDialog from "@/components/ShareProfileDialog";
import ShareDealDialog from "@/components/ShareDealDialog";
import { useAuth, API } from "@/App";

const UnifiedDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Active tab from URL or default
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'stats');
  
  // Profile & Stats
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({
    hire_requests: 0,
    active_gigs: 0,
    completed_gigs: 0,
    total_earnings: 0
  });
  const [balance, setBalance] = useState({ available: 0, pending: 0, total_earnings: 0 });
  
  // Talent Profile
  const [talentProfile, setTalentProfile] = useState(null);
  const [talentBookings, setTalentBookings] = useState([]);
  const [showTalentModal, setShowTalentModal] = useState(false);
  const [savingTalent, setSavingTalent] = useState(false);
  
  // Referral
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralLeaderboard, setReferralLeaderboard] = useState([]);
  
  // Messages (new unified chat)
  const [chatThreads, setChatThreads] = useState([]);
  const [chatTabView, setChatTabView] = useState("requests"); // 'requests' | 'chats'
  
  // Wallet & Withdrawals
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [processingWithdrawal, setProcessingWithdrawal] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({ amount: "", method: "upi" });
  const [counterOfferModal, setCounterOfferModal] = useState({ open: false, bookingId: null, amount: "", isOldSystem: false });
  const [paymentDetails, setPaymentDetails] = useState({
    upi_id: "",
    bank_account: "",
    ifsc_code: "",
    bank_name: "",
    account_holder: ""
  });
  const [paymentDetailsModalOpen, setPaymentDetailsModalOpen] = useState(false);
  
  // Categories
  const [categories, setCategories] = useState([]);
  const [pricing, setPricing] = useState({});
  
  // Talent Form
  const [talentForm, setTalentForm] = useState({
    category: "",
    bio: "",
    hourly_rate: "",
    project_rate: "",
    languages: ["Hindi", "English"],
    experience: "",
    city: "",
    photos: [],
    video_showreel: "",
    portfolio_links: [""],
    availability: "available",
    // Influencer-specific
    instagram_handle: "",
    youtube_channel: "",
    facebook_page: "",
    followers_count: ""
  });
  
  // Verified Badge Modal
  const [showVerifiedModal, setShowVerifiedModal] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  
  // Share Deal Dialog
  const [shareDealDialogOpen, setShareDealDialogOpen] = useState(false);
  const [shareDealData, setShareDealData] = useState(null);
  
  useEffect(() => {
    fetchAll();
    
    const pollInterval = setInterval(() => {
      fetchTalentBookings();
      fetchBalance();
    }, 10000);
    
    return () => clearInterval(pollInterval);
  }, []);
  
  // Open talent modal if coming from signup
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'talent' && !talentProfile) {
      setActiveTab('talent');
      setShowTalentModal(true);
    }
  }, [searchParams, talentProfile]);
  
  const fetchAll = async () => {
    await Promise.all([
      fetchProfile(),
      fetchTalentProfile(),
      fetchTalentBookings(),
      fetchBalance(),
      fetchReferralInfo(),
      fetchCategories(),
      fetchWithdrawals(),
      fetchPaymentDetails(),
      fetchConversations()
    ]);
  };
  
  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/influencer/profile`, { headers: getAuthHeaders() });
      setProfile(response.data);
      
      // Calculate stats
      setStats({
        hire_requests: response.data.collaboration_requests || 0,
        active_gigs: response.data.active_deals || 0,
        completed_gigs: response.data.closed_deals || 0,
        total_earnings: response.data.total_earnings || 0
      });
    } catch (error) {
      console.error("Failed to fetch profile");
    }
  };
  
  const fetchTalentProfile = async () => {
    try {
      const response = await axios.get(`${API}/talent/profile`, { headers: getAuthHeaders() });
      if (response.data.has_profile) {
        setTalentProfile(response.data.profile);
        // Pre-fill form
        const p = response.data.profile;
        setTalentForm({
          category: p.category || "",
          bio: p.bio || "",
          hourly_rate: p.hourly_rate || "",
          project_rate: p.project_rate || "",
          languages: p.languages || ["Hindi", "English"],
          experience: p.experience || "",
          city: p.city || "",
          photos: p.photos || [],
          video_showreel: p.video_showreel || "",
          portfolio_links: p.portfolio_links?.length > 0 ? p.portfolio_links : [""],
          availability: p.availability || "available",
          instagram_handle: p.instagram_handle || "",
          youtube_channel: p.youtube_channel || "",
          facebook_page: p.facebook_page || "",
          followers_count: p.followers_count || ""
        });
      }
    } catch (error) {
      console.error("Failed to fetch talent profile");
    }
  };
  
  const fetchTalentBookings = async () => {
    try {
      // Fetch both new talent bookings AND old collaboration requests
      const [bookingsRes, collabRes] = await Promise.all([
        axios.get(`${API}/talent/bookings/received`, { headers: getAuthHeaders() }).catch(() => ({ data: { bookings: [] } })),
        axios.get(`${API}/influencer/messages`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      
      const newBookings = bookingsRes.data.bookings || [];
      const oldCollabs = collabRes.data || [];
      
      // Convert old collaboration requests to booking format
      const convertedCollabs = oldCollabs.map(collab => {
        // Normalize legacy statuses: when brand has already paid, the linked deal
        // becomes "in_progress" but some collaboration_requests can be stuck on
        // "in_progress" (legacy data) instead of "paid". Treat both as "paid" so
        // the influencer sees the "Mark as Complete" action.
        let normalizedStatus = collab.status;
        if (normalizedStatus === "in_progress") {
          normalizedStatus = "paid";
        }
        return {
          booking_id: collab.request_id,
          request_id: collab.request_id, // Keep request_id for messaging
          deal_id: collab.deal_id, // Deal ID once brand has paid (for mark-complete)
          brand_name: collab.brand_name,
          brand_id: collab.brand_id,
          project_description: collab.message || "Collaboration Request",
          budget: collab.deal_amount || collab.current_offer || 0,
          status: normalizedStatus,
          raw_status: collab.status,
          created_at: collab.created_at,
          booking_type: "collaboration",
          is_old_system: true
        };
      });
      
      // Combine both
      const allBookings = [...newBookings, ...convertedCollabs];
      setTalentBookings(allBookings);
      
      // Update stats
      setStats(prev => ({
        ...prev,
        hire_requests: allBookings.filter(b => b.status === 'pending').length,
        active_gigs: allBookings.filter(b => ['accepted', 'paid', 'in_progress'].includes(b.status)).length,
        completed_gigs: allBookings.filter(b => b.status === 'completed').length
      }));
    } catch (error) {
      console.error("Failed to fetch bookings:", error);
    }
  };
  
  const fetchBalance = async () => {
    try {
      const response = await axios.get(`${API}/influencer/balance`, { headers: getAuthHeaders() });
      setBalance(response.data);
    } catch (error) {
      console.error("Failed to fetch balance");
    }
  };
  
  const fetchReferralInfo = async () => {
    try {
      const [infoRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/referral/info`, { headers: getAuthHeaders() }),
        axios.get(`${API}/referral/leaderboard`)
      ]);
      setReferralInfo(infoRes.data);
      setReferralLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (error) {
      console.error("Failed to fetch referral");
    }
  };
  
  // Messaging functions
  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/chat/threads`, { headers: getAuthHeaders() });
      setChatThreads(response.data.threads || []);
    } catch (error) {
      console.error("Failed to fetch chat threads");
    }
  };
  
  const fetchWithdrawals = async () => {
    try {
      const response = await axios.get(`${API}/influencer/withdrawals`, { headers: getAuthHeaders() });
      setWithdrawals(response.data || []);
    } catch (error) {
      console.error("Failed to fetch withdrawals");
    }
  };
  
  const fetchPaymentDetails = async () => {
    try {
      const response = await axios.get(`${API}/influencer/payment-details`, { headers: getAuthHeaders() });
      setPaymentDetails(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      // Ignore
    }
  };
  
  const handleSavePaymentDetails = async () => {
    try {
      await axios.put(`${API}/influencer/payment-details`, paymentDetails, { headers: getAuthHeaders() });
      toast.success("Payment details saved!");
      setPaymentDetailsModalOpen(false);
    } catch (error) {
      toast.error("Failed to save payment details");
    }
  };
  
  const handleWithdraw = async () => {
    if (!withdrawForm.amount || parseInt(withdrawForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (parseInt(withdrawForm.amount) > (balance.available || 0)) {
      toast.error("Insufficient balance");
      return;
    }
    
    setProcessingWithdrawal(true);
    try {
      await axios.post(`${API}/influencer/withdraw`, {
        amount: parseInt(withdrawForm.amount),
        payment_method: withdrawForm.method
      }, { headers: getAuthHeaders() });
      toast.success("Withdrawal request submitted! Admin will process it soon.");
      setWithdrawModalOpen(false);
      setWithdrawForm({ amount: "", method: "upi" });
      fetchBalance();
      fetchWithdrawals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request withdrawal");
    } finally {
      setProcessingWithdrawal(false);
    }
  };
  
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/talent/categories`);
      setCategories(response.data.categories || []);
      setPricing(response.data.pricing || {});
    } catch (error) {
      console.error("Failed to fetch categories");
    }
  };
  
  const handleVerifiedBadgeUpgrade = async () => {
    try {
      // Create Razorpay order for verified badge
      const response = await axios.post(`${API}/talent/purchase-verified-badge`, {}, { headers: getAuthHeaders() });
      
      const options = {
        key: response.data.key_id,
        amount: response.data.amount,
        currency: response.data.currency || "INR",
        name: "FOUNDSTAR",
        description: "Verified Creator Badge",
        order_id: response.data.order_id,
        handler: async (paymentResponse) => {
          try {
            const result = await axios.post(`${API}/talent/verify-badge-payment`, {
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature
            }, { headers: getAuthHeaders() });
            
            toast.success("🎉 Payment Successful! Your verification request has been submitted. Admin will review within 24-48 hours.");
            fetchTalentProfile();
          } catch (error) {
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        prefill: {
          email: user?.email || "",
          name: user?.name || talentProfile?.name || ""
        },
        theme: {
          color: "#7B2FBE"
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
    }
  };
  
  const saveTalentProfile = async () => {
    if (!talentForm.category) {
      toast.error("Please select a category");
      return;
    }
    if (!talentForm.bio || talentForm.bio.length < 20) {
      toast.error("Please write a bio (min 20 characters)");
      return;
    }
    
    setSavingTalent(true);
    try {
      const payload = {
        category: talentForm.category,
        bio: talentForm.bio,
        hourly_rate: 0, // Rate decided by brand
        project_rate: 0, // Rate decided by brand
        languages: talentForm.languages,
        experience: talentForm.experience,
        city: talentForm.city,
        portfolio_links: talentForm.portfolio_links.filter(l => l.trim() !== ""),
        availability: talentForm.availability,
        photos: talentForm.photos,
        video_showreel: talentForm.video_showreel,
        instagram_handle: talentForm.instagram_handle,
        youtube_channel: talentForm.youtube_channel,
        facebook_page: talentForm.facebook_page,
        followers_count: parseInt(talentForm.followers_count) || 0
      };
      
      await axios.post(`${API}/talent/profile`, payload, { headers: getAuthHeaders() });
      toast.success("🎉 Profile saved! You're now visible to brands.");
      setShowTalentModal(false);
      fetchTalentProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save profile");
    } finally {
      setSavingTalent(false);
    }
  };
  
  const respondToBooking = async (bookingId, action, isOldSystem = false) => {
    try {
      let response;
      if (isOldSystem) {
        // Old collaboration system - use influencer endpoints
        const endpoint = action === 'accept' 
          ? `${API}/influencer/request/${bookingId}/accept`
          : `${API}/influencer/request/${bookingId}/reject`;
        response = await axios.post(endpoint, {}, { headers: getAuthHeaders() });
        
        // If accepted, open messaging with the conversation
        if (action === 'accept' && response.data?.conversation_id) {
          toast.success("✅ Accepted! Opening chat...");
          // Navigate to messages or open message modal
          setTimeout(() => {
            setActiveTab("messages");
            fetchTalentBookings();
          }, 500);
          return;
        }
      } else {
        // New talent booking system
        response = await axios.put(`${API}/talent/booking/${bookingId}/respond?action=${action}`, {}, { headers: getAuthHeaders() });
      }
      toast.success(action === 'accept' ? "✅ Accepted! Brand will pay now." : "Declined");
      fetchTalentBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed");
    }
  };
  
  const markBookingComplete = async (bookingId, isOldSystem = false, dealId = null) => {
    try {
      if (isOldSystem) {
        // Old system uses deals endpoint. Use deal_id if available, else fall back to request_id (backend resolves).
        const idForApi = dealId || bookingId;
        await axios.post(`${API}/deals/${idForApi}/mark-complete`, {}, { headers: getAuthHeaders() });
      } else {
        await axios.put(`${API}/talent/booking/${bookingId}/complete`, {}, { headers: getAuthHeaders() });
      }
      toast.success("✅ Marked complete! Waiting for brand approval.");
      fetchTalentBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed");
    }
  };
  
  const handleCounterOffer = async (bookingId, amount, isOldSystem = false) => {
    try {
      if (isOldSystem) {
        await axios.post(`${API}/collaboration/${bookingId}/counter-offer`, 
          { amount: parseInt(amount) }, 
          { headers: getAuthHeaders() }
        );
      } else {
        await axios.post(`${API}/talent/booking/${bookingId}/counter-offer`, 
          { amount: parseInt(amount) }, 
          { headers: getAuthHeaders() }
        );
      }
      toast.success("Counter offer sent!");
      setCounterOfferModal({ open: false, bookingId: null, amount: "", isOldSystem: false });
      fetchTalentBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send counter offer");
    }
  };
  
  const copyReferralLink = () => {
    if (referralInfo?.referral_code) {
      navigator.clipboard.writeText(`${window.location.origin}/?ref=${referralInfo.referral_code}`);
      toast.success("Link copied!");
    }
  };
  
  const shareOnWhatsApp = () => {
    if (referralInfo?.referral_code) {
      const text = `Join FOUNDSTAR and earn money as a creator! Use my referral code: ${referralInfo.referral_code}. Sign up free: ${window.location.origin}/?ref=${referralInfo.referral_code}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };
  
  const formatCurrency = (amount) => {
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount}`;
  };
  
  const getCategoryIcon = (id) => {
    const icons = {
      models: Camera,
      actors: Film,
      dancers: Music,
      voice_artists: Mic,
      photographers: Camera,
      content_writers: PenTool,
      influencers: Smartphone,
      editors: Clapperboard,
      gaming: Gamepad2
    };
    return icons[id] || Camera;
  };
  
  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] text-white p-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10 border-2 border-white/30">
              <AvatarImage src={user?.picture || profile?.picture} />
              <AvatarFallback className="bg-white/20 text-white">{user?.name?.[0]}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold">Hi, {user?.name?.split(' ')[0]}</p>
              <p className="text-xs text-white/80">
                {talentProfile ? categories.find(c => c.id === talentProfile.category)?.name : "Complete your profile"}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {talentProfile?.is_verified && (
              <Badge className="bg-white/20 text-white">
                <BadgeCheck className="w-3 h-3 mr-1" />
                Pro
              </Badge>
            )}
            {/* Share Profile button - only show if profile is created */}
            {talentProfile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShareDialogOpen(true)}
                className="text-white hover:bg-white/20"
                title="Share your profile"
                data-testid="star-share-profile-btn"
              >
                <Share2 className="w-5 h-5" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-white hover:bg-white/20">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4">
              <Package className="w-8 h-8 text-[#FF69B4] mb-2" />
              <p className="text-2xl font-bold text-[#1E293B]">{stats.hire_requests}</p>
              <p className="text-sm text-gray-500">Hire Requests</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <Clock className="w-8 h-8 text-[#7B2FBE] mb-2" />
              <p className="text-2xl font-bold text-[#1E293B]">{stats.active_gigs}</p>
              <p className="text-sm text-gray-500">Active Gigs</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <CheckCircle className="w-8 h-8 text-[#00A86B] mb-2" />
              <p className="text-2xl font-bold text-[#1E293B]">{stats.completed_gigs}</p>
              <p className="text-sm text-gray-500">Completed</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <Wallet className="w-8 h-8 text-[#FFD700] mb-2" />
              <p className="text-2xl font-bold text-[#1E293B]">{formatCurrency(balance.total_earnings || 0)}</p>
              <p className="text-sm text-gray-500">Total Earnings</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 bg-white rounded-xl p-1 shadow-sm mb-6">
            <TabsTrigger value="stats" className="data-[state=active]:bg-[#FF69B4] data-[state=active]:text-white rounded-lg">
              My Deals
            </TabsTrigger>
            <TabsTrigger value="wallet" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#7B2FBE] data-[state=active]:to-[#FF4D6D] data-[state=active]:text-white rounded-lg">
              <Wallet className="w-4 h-4 mr-1" />
              Wallet
            </TabsTrigger>
            <TabsTrigger value="referral" className="data-[state=active]:bg-[#00A86B] data-[state=active]:text-white rounded-lg">
              <Gift className="w-4 h-4 mr-1" />
              Refer & Earn
            </TabsTrigger>
          </TabsList>

          {/* My Deals Tab */}
          <TabsContent value="stats" className="space-y-6">
            {/* Sub-tabs for Requests and Messages */}
            <div className="flex gap-2 mb-4">
              <Button 
                variant={chatTabView === "requests" ? "default" : "outline"}
                size="sm"
                onClick={() => setChatTabView("requests")}
                className={chatTabView === "requests" ? "bg-[#7B2FBE] hover:bg-[#6B25A8]" : ""}
                data-testid="star-requests-tab-btn"
              >
                <Package className="w-4 h-4 mr-1" />
                Requests ({talentBookings.length})
              </Button>
              <Button 
                variant={chatTabView === "chats" ? "default" : "outline"}
                size="sm"
                onClick={() => setChatTabView("chats")}
                className={chatTabView === "chats" ? "bg-[#3B82F6] hover:bg-[#2563EB]" : ""}
                data-testid="star-chats-tab-btn"
              >
                <MessageSquare className="w-4 h-4 mr-1" />
                Chats ({chatThreads.length})
              </Button>
            </div>

            {/* Show Chat Panel or Requests */}
            {chatTabView === "chats" ? (
              <ChatPanel accentColor="#7B2FBE" emptyTitle="No chats yet" emptySubtitle="Brand messages and chats from your deals will appear here." />
            ) : (
              <>
                {/* Booking Requests */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg text-[#1E293B] mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#7B2FBE]" />
                  Booking Requests
                  {talentBookings.filter(b => b.status === 'pending').length > 0 && (
                    <Badge className="bg-red-500 text-white ml-2">
                      {talentBookings.filter(b => b.status === 'pending').length} New
                    </Badge>
                  )}
                </h3>

                {talentBookings.length === 0 ? (
                  <div className="text-center py-10 text-gray-500">
                    <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No booking requests yet</p>
                    <p className="text-sm">Complete your talent profile to get hired!</p>
                    {!talentProfile && (
                      <Button 
                        onClick={() => { setActiveTab('talent'); setShowTalentModal(true); }}
                        className="mt-4 bg-[#FF69B4]"
                      >
                        Create Profile
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {talentBookings.map((booking) => (
                      <div key={booking.booking_id} className="border rounded-xl p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Building2 className="w-4 h-4 text-[#7B2FBE]" />
                              <span className="font-semibold text-[#1E293B]">{booking.brand_name}</span>
                              <Badge className={
                                booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                booking.status === 'accepted' ? 'bg-blue-100 text-blue-700' :
                                booking.status === 'paid' ? 'bg-green-100 text-green-700' :
                                booking.status === 'pending_approval' ? 'bg-purple-100 text-purple-700' :
                                booking.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                'bg-gray-100 text-gray-700'
                              }>
                                {booking.status === 'pending' ? 'New Request' :
                                 booking.status === 'accepted' ? 'Waiting Payment' :
                                 booking.status === 'paid' ? 'In Progress' :
                                 booking.status === 'pending_approval' ? 'Pending Approval' :
                                 booking.status === 'completed' ? 'Completed' :
                                 booking.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 line-clamp-2">{booking.project_description}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-[#00A86B]">
                              {booking.is_barter ? (
                                <span className="flex items-center justify-end gap-1">
                                  🎁 ₹{booking.budget?.toLocaleString()} <span className="text-xs font-normal text-purple-600">(Barter)</span>
                                </span>
                              ) : (
                                `₹${booking.budget?.toLocaleString()}`
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {booking.date_required 
                                ? new Date(booking.date_required).toLocaleDateString() 
                                : booking.created_at 
                                  ? new Date(booking.created_at).toLocaleDateString()
                                  : ""}
                            </p>
                          </div>
                        </div>

                        {booking.special_requirements && (
                          <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm">
                            <p className="text-gray-600"><strong>Requirements:</strong> {booking.special_requirements}</p>
                          </div>
                        )}

                        {booking.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              onClick={() => respondToBooking(booking.booking_id, 'accept', booking.is_old_system)}
                              className="flex-1 bg-[#00A86B] hover:bg-[#008F5B]"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Accept
                            </Button>
                            <Button
                              onClick={() => setCounterOfferModal({ open: true, bookingId: booking.booking_id, amount: booking.budget?.toString() || "", isOldSystem: booking.is_old_system })}
                              variant="outline"
                              className="flex-1 border-[#7B2FBE] text-[#7B2FBE] hover:bg-purple-50"
                            >
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Counter
                            </Button>
                            <Button
                              onClick={() => respondToBooking(booking.booking_id, 'reject', booking.is_old_system)}
                              variant="outline"
                              className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Decline
                            </Button>
                          </div>
                        )}
                        
                        {booking.status === 'negotiating' && (
                          <div className="flex gap-2">
                            <Button
                              onClick={() => respondToBooking(booking.booking_id, 'accept', booking.is_old_system)}
                              className="flex-1 bg-[#00A86B] hover:bg-[#008F5B]"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Accept Offer
                            </Button>
                            <Button
                              onClick={() => setCounterOfferModal({ open: true, bookingId: booking.booking_id, amount: booking.budget?.toString() || "", isOldSystem: booking.is_old_system })}
                              variant="outline"
                              className="flex-1 border-[#7B2FBE] text-[#7B2FBE] hover:bg-purple-50"
                            >
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Counter
                            </Button>
                          </div>
                        )}

                        {booking.status === 'paid' && (
                          <Button
                            onClick={() => markBookingComplete(booking.booking_id, booking.is_old_system, booking.deal_id)}
                            className="w-full bg-[#7B2FBE] hover:bg-[#6B25A8]"
                          >
                            <CheckCheck className="w-4 h-4 mr-1" />
                            Mark as Complete
                          </Button>
                        )}

                        {booking.status === 'completed' && booking.escrow_status === 'released' && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-[#00A86B] bg-green-50 p-3 rounded-lg">
                              <CheckCircle className="w-5 h-5" />
                              <span className="font-medium">Payment released: ₹{booking.payout_amount?.toLocaleString()}</span>
                            </div>
                            {/* Share Deal Button for Star */}
                            <Button
                              onClick={() => {
                                setShareDealData({
                                  brand: {
                                    name: booking.brand_name || booking.requester_name,
                                    picture: booking.brand_photo || booking.requester_photo,
                                    company_name: booking.brand_company
                                  },
                                  star: {
                                    user_id: user?.user_id,
                                    name: talentProfile?.name || profile?.name,
                                    picture: talentProfile?.picture || profile?.picture,
                                    category: talentProfile?.category || "Creator",
                                    is_verified: talentProfile?.is_verified
                                  },
                                  dealAmount: booking.amount || booking.final_amount
                                });
                                setShareDealDialogOpen(true);
                              }}
                              variant="outline"
                              className="w-full rounded-full border-[#FF69B4] text-[#FF69B4] hover:bg-[#FF69B4] hover:text-white"
                              data-testid="star-share-deal-btn"
                            >
                              <Share2 className="w-4 h-4 mr-2" />
                              Share This Collab
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Chat Preview (switch to Chats tab for full view) */}
            {chatThreads.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-blue-500" />
                    Brand Chats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {chatThreads.slice(0, 5).map((t) => (
                    <div 
                      key={t.thread_id}
                      onClick={() => setChatTabView("chats")}
                      className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-r from-[#7B2FBE] to-[#FF69B4] flex items-center justify-center text-white font-bold text-sm">
                          {t.other_user?.name?.charAt(0) || 'B'}
                        </div>
                        <div>
                          <p className="font-medium text-sm text-[#1E293B]">{t.other_user?.name || 'Brand'}</p>
                          <p className="text-xs text-gray-500 truncate max-w-[180px]">
                            {t.last_message || 'Start the conversation...'}
                          </p>
                        </div>
                      </div>
                      {t.unread_count > 0 && (
                        <span className="min-w-[20px] h-[20px] bg-[#FF69B4] text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
                          {t.unread_count}
                        </span>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
              </>
            )}
          </TabsContent>

          {/* Wallet Tab */}
          <TabsContent value="wallet" className="space-y-6">
            {/* Balance Card */}
            <Card className="bg-gradient-to-r from-[#7B2FBE] to-[#FF4D6D] text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-white/80 text-sm">Available Balance</p>
                    <p className="text-4xl font-bold">{formatCurrency(balance.available || 0)}</p>
                  </div>
                  <Wallet className="w-16 h-16 text-white/30" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white/20 rounded-lg p-3 text-center">
                    <p className="text-white/70 text-xs">Pending</p>
                    <p className="font-bold text-lg">{formatCurrency(balance.pending || 0)}</p>
                  </div>
                  <div className="bg-white/20 rounded-lg p-3 text-center">
                    <p className="text-white/70 text-xs">Total Earned</p>
                    <p className="font-bold text-lg">{formatCurrency(balance.total_earnings || 0)}</p>
                  </div>
                  <div className="bg-white/20 rounded-lg p-3 text-center">
                    <p className="text-white/70 text-xs">Withdrawn</p>
                    <p className="font-bold text-lg">{formatCurrency(balance.withdrawn || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={() => setWithdrawModalOpen(true)}
                disabled={!balance.available || balance.available <= 0}
                className="bg-[#00A86B] hover:bg-[#008B5C] text-white py-6 rounded-xl text-lg"
              >
                <IndianRupee className="w-5 h-5 mr-2" />
                Withdraw
              </Button>
              <Button
                onClick={() => setPaymentDetailsModalOpen(true)}
                variant="outline"
                className="border-[#7B2FBE] text-[#7B2FBE] hover:bg-purple-50 py-6 rounded-xl text-lg"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Payment Details
              </Button>
            </div>

            {/* Withdrawal History */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg text-[#1E293B] mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#7B2FBE]" />
                  Withdrawal History
                </h3>
                
                {withdrawals.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Wallet className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No withdrawals yet</p>
                    <p className="text-sm">Complete gigs to earn money</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {withdrawals.map((w) => (
                      <div key={w.withdrawal_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            w.status === 'completed' ? 'bg-green-100' :
                            w.status === 'pending' ? 'bg-yellow-100' :
                            'bg-red-100'
                          }`}>
                            {w.status === 'completed' ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                             w.status === 'pending' ? <Clock className="w-5 h-5 text-yellow-600" /> :
                             <XCircle className="w-5 h-5 text-red-600" />}
                          </div>
                          <div>
                            <p className="font-medium text-[#1E293B]">₹{w.amount?.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">{new Date(w.created_at).toLocaleDateString()} • {w.payment_method?.toUpperCase()}</p>
                          </div>
                        </div>
                        <Badge className={
                          w.status === 'completed' ? 'bg-green-100 text-green-700' :
                          w.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }>
                          {w.status === 'completed' ? 'Completed' :
                           w.status === 'pending' ? 'Processing' :
                           'Failed'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* How Payments Work */}
            <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-0">
              <CardContent className="p-6">
                <h4 className="font-semibold text-[#7B2FBE] mb-4">How Payments Work</h4>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#7B2FBE] text-white flex items-center justify-center text-xs flex-shrink-0">1</div>
                    <p>Brand books you and pays to escrow (platform holds the money)</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#7B2FBE] text-white flex items-center justify-center text-xs flex-shrink-0">2</div>
                    <p>You complete the work and mark it as done</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#7B2FBE] text-white flex items-center justify-center text-xs flex-shrink-0">3</div>
                    <p>Brand approves → Money moves to your wallet (minus 5% platform fee)</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00A86B] text-white flex items-center justify-center text-xs flex-shrink-0">4</div>
                    <p>Withdraw to your UPI or bank anytime!</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Referral Tab */}
          <TabsContent value="referral" className="space-y-6">
            <Card className="bg-gradient-to-r from-[#00A86B] to-[#34D399] text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <Gift className="w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="font-bold text-xl">Refer & Earn ₹20</h2>
                      <p className="text-white/80 text-sm">Invite friends, earn rewards</p>
                    </div>
                  </div>
                  {referralInfo && (
                    <div className="bg-white/20 rounded-xl px-4 py-2 text-center">
                      <p className="text-white/70 text-xs">Your Code</p>
                      <p className="font-bold text-lg">{referralInfo.referral_code}</p>
                    </div>
                  )}
                </div>

                {referralInfo && (
                  <>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="bg-white/10 rounded-xl p-3 text-center">
                        <p className="text-white/70 text-xs mb-1">Referred</p>
                        <p className="font-bold text-xl">{referralInfo.total_referrals || 0}</p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-3 text-center">
                        <p className="text-white/70 text-xs mb-1">Verified</p>
                        <p className="font-bold text-xl">{referralInfo.successful_referrals || 0}</p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-3 text-center">
                        <p className="text-white/70 text-xs mb-1">Earned</p>
                        <p className="font-bold text-xl">₹{referralInfo.total_earnings || 0}</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={copyReferralLink} className="flex-1 bg-white/20 hover:bg-white/30 text-white border-0">
                        <Copy className="w-4 h-4 mr-1" />
                        Copy Link
                      </Button>
                      <Button onClick={shareOnWhatsApp} className="flex-1 bg-[#25D366] hover:bg-[#20BD5A] text-white border-0">
                        <Share2 className="w-4 h-4 mr-1" />
                        WhatsApp
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Leaderboard */}
            {referralLeaderboard.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg text-[#1E293B] mb-4 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[#FFD700]" />
                    Top Referrers
                  </h3>
                  <div className="space-y-3">
                    {referralLeaderboard.slice(0, 5).map((ref, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          i === 0 ? 'bg-[#FFD700] text-white' : 
                          i === 1 ? 'bg-[#C0C0C0] text-white' : 
                          i === 2 ? 'bg-[#CD7F32] text-white' : 
                          'bg-gray-200 text-gray-600'
                        }`}>
                          {i + 1}
                        </span>
                        <span className="flex-1 font-medium text-[#1E293B]">{ref.name}</span>
                        <span className="font-bold text-[#00A86B]">{ref.count} referrals</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

        </Tabs>
      </main>

      {/* Star Profile Modal */}
      <Dialog open={showTalentModal} onOpenChange={setShowTalentModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#7B2FBE] to-[#FF4D6D] rounded-t-lg"></div>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#7B2FBE]" />
              {talentProfile ? 'Edit Profile' : 'Create Star Profile'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Category Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Select Category *</Label>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => {
                  const Icon = getCategoryIcon(cat.id);
                  const isSelected = talentForm.category === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setTalentForm({...talentForm, category: cat.id})}
                      className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 text-left ${
                        isSelected ? 'border-[#FF69B4] bg-pink-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-5 h-5" style={{ color: cat.color }} />
                      <div>
                        <p className="font-medium text-sm">{cat.name}</p>
                        <p className="text-xs text-gray-500">{cat.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Influencer-specific fields */}
            {talentForm.category === 'influencers' && (
              <div className="p-4 bg-pink-50 rounded-xl space-y-3">
                <p className="text-sm font-semibold text-[#E91E63] flex items-center gap-2">
                  <Smartphone className="w-4 h-4" /> Social Media Details
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600">Instagram Handle</Label>
                    <Input
                      placeholder="@yourusername"
                      value={talentForm.instagram_handle}
                      onChange={(e) => setTalentForm({...talentForm, instagram_handle: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Followers Count</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 10000"
                      value={talentForm.followers_count}
                      onChange={(e) => setTalentForm({...talentForm, followers_count: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">YouTube Channel (optional)</Label>
                  <Input
                    placeholder="Channel URL"
                    value={talentForm.youtube_channel}
                    onChange={(e) => setTalentForm({...talentForm, youtube_channel: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            {/* Bio */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Bio / About You *</Label>
              <Textarea
                placeholder="Describe your experience, skills, and what makes you unique..."
                value={talentForm.bio}
                onChange={(e) => setTalentForm({...talentForm, bio: e.target.value})}
                className="min-h-[100px]"
              />
            </div>

            {/* City */}
            <div>
              <Label className="text-sm font-medium mb-2 block">City</Label>
              <Input
                placeholder="e.g., Mumbai"
                value={talentForm.city}
                onChange={(e) => setTalentForm({...talentForm, city: e.target.value})}
              />
            </div>


            {/* Experience */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Experience</Label>
              <Select value={talentForm.experience} onValueChange={(v) => setTalentForm({...talentForm, experience: v})}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fresher">Fresher (0-1 year)</SelectItem>
                  <SelectItem value="1-3 years">1-3 years</SelectItem>
                  <SelectItem value="3-5 years">3-5 years</SelectItem>
                  <SelectItem value="5+ years">5+ years</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Languages */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Languages</Label>
              <div className="flex flex-wrap gap-2">
                {['Hindi', 'English', 'Marathi', 'Gujarati', 'Tamil', 'Telugu', 'Bengali', 'Kannada'].map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => {
                      const langs = talentForm.languages || [];
                      if (langs.includes(lang)) {
                        setTalentForm({...talentForm, languages: langs.filter(l => l !== lang)});
                      } else {
                        setTalentForm({...talentForm, languages: [...langs, lang]});
                      }
                    }}
                    className={`px-3 py-1 rounded-full text-sm border transition-all ${
                      talentForm.languages?.includes(lang) 
                        ? 'bg-[#7B2FBE] text-white border-[#7B2FBE]' 
                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#7B2FBE]'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>

            {/* Portfolio Links */}
            <div>
              <Label className="text-sm font-medium mb-2 block flex items-center gap-1">
                <Globe className="w-4 h-4" /> Portfolio Links
              </Label>
              {talentForm.portfolio_links.map((link, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input
                    placeholder="https://..."
                    value={link}
                    onChange={(e) => {
                      const newLinks = [...talentForm.portfolio_links];
                      newLinks[i] = e.target.value;
                      setTalentForm({...talentForm, portfolio_links: newLinks});
                    }}
                  />
                  {i === talentForm.portfolio_links.length - 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setTalentForm({...talentForm, portfolio_links: [...talentForm.portfolio_links, '']})}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Free Badge */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
              <p className="text-green-700 font-medium">Free to Create!</p>
              <p className="text-green-600 text-sm">No signup fees. Start getting hired immediately.</p>
            </div>

            {/* Save Button */}
            <Button 
              onClick={saveTalentProfile}
              disabled={savingTalent}
              className="w-full bg-gradient-to-r from-[#7B2FBE] to-[#FF4D6D] hover:opacity-90 text-white py-6 rounded-full text-lg font-semibold"
            >
              {savingTalent ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {talentProfile ? 'Update Profile' : 'Create Profile & Go Live'}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdraw Modal */}
      <Dialog open={withdrawModalOpen} onOpenChange={setWithdrawModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-[#00A86B]" />
              Withdraw Funds
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-600">Available Balance</p>
              <p className="text-3xl font-bold text-[#00A86B]">{formatCurrency(balance.available || 0)}</p>
            </div>
            
            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                placeholder="Enter amount"
                value={withdrawForm.amount}
                onChange={(e) => setWithdrawForm({...withdrawForm, amount: e.target.value})}
                max={balance.available || 0}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Payment Method</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setWithdrawForm({...withdrawForm, method: 'upi'})}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    withdrawForm.method === 'upi' 
                      ? 'border-[#00A86B] bg-green-50' 
                      : 'border-gray-200 hover:border-[#00A86B]'
                  }`}
                >
                  <p className="font-medium">UPI</p>
                  <p className="text-xs text-gray-500">Instant transfer</p>
                </button>
                <button
                  type="button"
                  onClick={() => setWithdrawForm({...withdrawForm, method: 'bank'})}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    withdrawForm.method === 'bank' 
                      ? 'border-[#00A86B] bg-green-50' 
                      : 'border-gray-200 hover:border-[#00A86B]'
                  }`}
                >
                  <p className="font-medium">Bank Transfer</p>
                  <p className="text-xs text-gray-500">1-2 business days</p>
                </button>
              </div>
            </div>
            
            <Button
              onClick={handleWithdraw}
              disabled={processingWithdrawal || !withdrawForm.amount || parseInt(withdrawForm.amount) <= 0}
              className="w-full bg-[#00A86B] hover:bg-[#008B5C]"
            >
              {processingWithdrawal ? "Processing..." : `Withdraw ₹${withdrawForm.amount || 0}`}
            </Button>
            
            <p className="text-xs text-gray-500 text-center">
              Withdrawals are processed within 24-48 hours
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Details Modal */}
      <Dialog open={paymentDetailsModalOpen} onOpenChange={setPaymentDetailsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#7B2FBE]" />
              Payment Details
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>UPI ID</Label>
              <Input
                placeholder="yourname@paytm"
                value={paymentDetails.upi_id}
                onChange={(e) => setPaymentDetails({...paymentDetails, upi_id: e.target.value})}
                className="mt-1"
              />
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-600 mb-3">Bank Account (Optional)</p>
              
              <div className="space-y-3">
                <div>
                  <Label>Account Holder Name</Label>
                  <Input
                    placeholder="John Doe"
                    value={paymentDetails.account_holder}
                    onChange={(e) => setPaymentDetails({...paymentDetails, account_holder: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Bank Name</Label>
                  <Input
                    placeholder="HDFC Bank"
                    value={paymentDetails.bank_name}
                    onChange={(e) => setPaymentDetails({...paymentDetails, bank_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Account Number</Label>
                  <Input
                    placeholder="1234567890"
                    value={paymentDetails.bank_account}
                    onChange={(e) => setPaymentDetails({...paymentDetails, bank_account: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>IFSC Code</Label>
                  <Input
                    placeholder="HDFC0001234"
                    value={paymentDetails.ifsc_code}
                    onChange={(e) => setPaymentDetails({...paymentDetails, ifsc_code: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            <Button
              onClick={handleSavePaymentDetails}
              className="w-full bg-[#7B2FBE] hover:bg-[#6B25A8]"
            >
              Save Payment Details
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Counter Offer Modal */}
      <Dialog open={counterOfferModal.open} onOpenChange={(open) => setCounterOfferModal({ ...counterOfferModal, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#7B2FBE]" />
              Send Counter Offer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Your Counter Offer Amount (₹)</Label>
              <Input
                type="number"
                placeholder="Enter your amount"
                value={counterOfferModal.amount}
                onChange={(e) => setCounterOfferModal({ ...counterOfferModal, amount: e.target.value })}
                className="mt-1 text-lg font-semibold"
              />
              <p className="text-xs text-gray-500 mt-1">Enter the amount you would like to receive for this project</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => handleCounterOffer(counterOfferModal.bookingId, counterOfferModal.amount, counterOfferModal.isOldSystem)}
                className="flex-1 bg-[#7B2FBE] hover:bg-[#6B25A8]"
                disabled={!counterOfferModal.amount || parseInt(counterOfferModal.amount) <= 0}
              >
                <Send className="w-4 h-4 mr-1" />
                Send Offer
              </Button>
              <Button
                variant="outline"
                onClick={() => setCounterOfferModal({ open: false, bookingId: null, amount: "", isOldSystem: false })}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav userType="influencer" />

      {/* Share Profile Dialog */}
      <ShareProfileDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        star={talentProfile ? {
          user_id: user?.user_id,
          name: talentProfile.name || user?.name || "Star",
          photo_url: talentProfile.picture || (talentProfile.photos && talentProfile.photos[0]) || talentProfile.photo_url || user?.picture || null,
          // Always pretty-print category from the categories list
          primary_category: categories.find(c => c.id === talentProfile.category)?.name || talentProfile.category || "Creator",
          categories: talentProfile.categories || [talentProfile.category].filter(Boolean),
          // Niches come from the influencer profile, not the talent profile
          niches: profile?.niches || [],
          bio: talentProfile.bio,
          is_verified: !!talentProfile.is_verified,
          is_pro_creator: !!talentProfile.is_pro_creator,
          // Precise location from the talent (Star Profile) record
          location: talentProfile.location || profile?.location || "",
          village: talentProfile.village || profile?.village || "",
          city: talentProfile.city || profile?.city || "",
          district: talentProfile.district || profile?.district || "",
          state: talentProfile.state || profile?.state || "",
          stats: {
            followers: talentProfile.followers_count || 0,
            rating: talentProfile.rating || talentProfile.trust_score || 0,
            completed_collabs: talentProfile.bookings_completed || talentProfile.closed_deals || 0,
          },
        } : null}
      />
      
      {/* Share Deal Dialog */}
      <ShareDealDialog
        open={shareDealDialogOpen}
        onClose={() => setShareDealDialogOpen(false)}
        dealData={shareDealData}
      />
    </div>
  );
};

export default UnifiedDashboard;
